import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image } from 'react-native';
import { IActivity, KeyStage, InputMode } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { SkiaCanvas } from './SkiaCanvas';
import { SvgTemplateOverlay } from './SvgTemplateOverlay';
import { ActivityMediaRenderer } from './ActivityMediaRenderer';
import { MultiModalInputArea } from './MultiModalInputArea';
import { useActivityInputModes } from '../hooks/useActivityInputModes';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { AudioPromptButton } from './AudioPromptButton';

interface CanvasPromptActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
    stimulusImageUrl?: string;
    prompt?: string;
    vocabularyBank?: string[];
}

/**
 * CanvasPromptActivity — reusable full-panel canvas + prompt + stimulus component.
 *
 * Used by foundation-subject activities:
 *   science_observation, history_source, geography_fieldnote,
 *   art_design_sketch, computing_design, re_reflection.
 *
 * Layout:
 *   1. Compact header with title, instructions, and audio speech button.
 *   2. Optional valid stimulus image & prompt callout.
 *   3. Optional vocabulary bank chips.
 *   4. Skia handwriting/drawing canvas filling the full size of the panel.
 *   5. Clear / Submit footer with 3D tactile controls.
 */
export const CanvasPromptActivity: React.FC<CanvasPromptActivityProps> = ({
    activity,
    onComplete,
    keyStage,
    stimulusImageUrl,
    prompt,
    vocabularyBank,
}) => {
    const media = activity.media || [];
    const displayPrompt = prompt || activity.config?.prompt || activity.instructions || 'Draw or write your response below.';
    const rawImageUrl = stimulusImageUrl || activity.config?.stimulusImageUrl || activity.config?.sourceImageUrl || activity.config?.mapImageUrl;

    // Filter out mock domains like example.com or invalid placeholders
    const imageUrl = useMemo(() => {
        if (!rawImageUrl || typeof rawImageUrl !== 'string') return null;
        const lower = rawImageUrl.trim().toLowerCase();
        if (lower.includes('example.com') || lower.includes('placeholder')) return null;
        if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('data:') || lower.startsWith('file://')) {
            return rawImageUrl.trim();
        }
        return null;
    }, [rawImageUrl]);

    const vocab = vocabularyBank || activity.config?.vocabularyBank || [];

    const { completedStrokes, clearStrokes } = useStrokeStore();
    const highContrast = useAccessibilityStore((s) => s.highContrast);
    const [textValue, setTextValue] = useState('');
    const [activeMode, setActiveMode] = useState<InputMode>('canvas');

    const flags = useFeatureFlags();
    const { allowedModes, defaultMode } = useActivityInputModes(activity);
    const multiModalEnabled = flags.inputModeSwitcherEnabled && allowedModes.length > 1;

    const canSubmit = multiModalEnabled
        ? (activeMode === 'canvas' ? completedStrokes.length > 0 : textValue.trim().length > 0)
        : completedStrokes.length > 0;

    const stageStyles = useMemo(() => {
        if (keyStage === 'EYFS') {
            return {
                title: { fontSize: 26 },
                imageHeight: 240,
                promptText: { fontSize: 18, lineHeight: 28 },
                vocabChipText: { fontSize: 16 },
                submitTxt: { fontSize: 26 },
            };
        }
        if (keyStage === 'KS2') {
            return {
                title: { fontSize: 20 },
                imageHeight: 160,
                promptText: { fontSize: 15, lineHeight: 22 },
                vocabChipText: { fontSize: 13 },
                submitTxt: { fontSize: 20 },
            };
        }
        // KS1 / default
        return {
            title: { fontSize: 22 },
            imageHeight: 180,
            promptText: { fontSize: 16, lineHeight: 24 },
            vocabChipText: { fontSize: 14 },
            submitTxt: { fontSize: 22 },
        };
    }, [keyStage]);

    return (
        <View style={[styles.container, highContrast && styles.containerHighContrast]}>
            {/* Scrollable content area (image + prompt + vocab) */}
            <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
                {/* Header with Audio Button for Year 1 / Pre-readers */}
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <View style={styles.headerTextWrap}>
                            <Text style={[styles.title, stageStyles.title, highContrast && styles.titleHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                            {activity.instructions && (
                                <Text style={[styles.instructions, highContrast && styles.instructionsHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                            )}
                        </View>
                        <AudioPromptButton text={`${activity.title}. ${displayPrompt}`} size={44} />
                    </View>
                </View>

                {/* Stimulus image from config (only if valid, never example.com) */}
                {imageUrl && (
                    <View style={[styles.imageBox, highContrast && styles.imageBoxHighContrast]}>
                        <Image source={{ uri: imageUrl }} style={[styles.stimulusImage, { height: stageStyles.imageHeight }]} resizeMode="contain" />
                    </View>
                )}

                {/* Supporting media from activity.media */}
                <ActivityMediaRenderer media={media} placement="before" />

                {/* Response prompt callout */}
                <View style={[styles.promptBox, highContrast && styles.promptBoxHighContrast]}>
                    <Text style={[styles.promptLabel, highContrast && styles.promptLabelHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️ Your task:</Text>
                    <Text style={[styles.promptText, stageStyles.promptText, highContrast && styles.promptTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{displayPrompt}</Text>
                </View>

                {/* Vocabulary bank chips (strictly omitted for EYFS pre-readers) */}
                {vocab.length > 0 && keyStage !== 'EYFS' && (
                    <View testID="canvas-vocab-container" style={styles.vocabContainer}>
                        <Text style={[styles.vocabLabel, highContrast && styles.vocabLabelHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>💡 Words to use:</Text>
                        <View style={styles.vocabRow}>
                            {vocab.map((word: string, i: number) => (
                                <View key={i} style={[styles.vocabChip, highContrast && styles.vocabChipHighContrast]}>
                                    <Text style={[styles.vocabChipText, stageStyles.vocabChipText, highContrast && styles.vocabChipTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{word}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                )}

                <ActivityMediaRenderer media={media} placement="alongside" />
            </ScrollView>

            {/* Input area - Full width and maximum available height */}
            {multiModalEnabled ? (
                <View testID="canvas-prompt-input-area" style={[styles.canvasArea, highContrast && styles.canvasAreaHighContrast]}>
                    <MultiModalInputArea
                        allowedModes={allowedModes}
                        defaultMode={defaultMode}
                        onChange={(val) => setTextValue(val)}
                        onSubmit={(val, mode, meta) => {
                            setTextValue(val);
                            if (meta?.voiceTranscript) {
                                useStrokeStore.getState().setStructuredData({
                                    voiceTranscript: meta.voiceTranscript,
                                    inputModeUsed: mode,
                                });
                            }
                            onComplete();
                        }}
                        onModeChange={(mode) => setActiveMode(mode)}
                        placeholder="Type or speak your response..."
                    />
                </View>
            ) : (
                <View testID="canvas-prompt-area" style={[styles.canvasArea, highContrast && styles.canvasAreaHighContrast]}
                    accessibilityRole="image"
                    accessibilityLabel="Handwriting canvas for writing your response"
                >
                    <SkiaCanvas />
                    {Boolean(activity.config?.svgTemplateUrl) && (
                        <SvgTemplateOverlay
                            svgTemplateUrl={activity.config?.svgTemplateUrl}
                            targetText={(activity.config as any)?.targetText || activity.title}
                        />
                    )}
                </View>
            )}

            {/* Footer controls */}
            <View style={styles.footer}>
                {(!multiModalEnabled || activeMode === 'canvas') && (
                    <TouchableOpacity
                        style={[styles.clearBtn, highContrast && styles.clearBtnHighContrast]}
                        onPress={clearStrokes}
                        accessibilityRole="button"
                        accessibilityLabel="Clear"
                        accessibilityHint="Clears all strokes from the canvas"
                        activeOpacity={0.7}
                    >
                        <Text style={[styles.clearTxt, highContrast && styles.clearTxtHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                    </TouchableOpacity>
                )}
                <TouchableOpacity
                    style={[styles.submitBtn, !canSubmit && styles.disabledBtn]}
                    onPress={onComplete}
                    disabled={!canSubmit}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your response"
                    accessibilityState={{ disabled: !canSubmit }}
                    activeOpacity={0.8}
                >
                    <Text style={[styles.submitTxt, stageStyles.submitTxt]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 16,
    },
    containerHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },

    scrollArea: {
        flexGrow: 0,
        flexShrink: 1,
        maxHeight: '34%',
        marginBottom: 8,
    },
    scrollContent: {
        paddingHorizontal: 4,
        paddingTop: 2,
        paddingBottom: 4,
        gap: 8,
    },

    header: { marginBottom: 4 },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
    },
    headerTextWrap: { flex: 1 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B', marginBottom: 2 },
    titleHighContrast: { color: '#FFFFFF' },
    instructions: { fontSize: 15, color: '#64748B', fontStyle: 'italic' },
    instructionsHighContrast: { color: '#FFFF00' },

    imageBox: {
        backgroundColor: '#F8FAFC',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        padding: 6,
        alignItems: 'center',
    },
    imageBoxHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
    },
    stimulusImage: {
        width: '100%',
        height: 160,
        borderRadius: 8,
    },

    promptBox: {
        backgroundColor: '#EEF2FF',
        borderRadius: 14,
        borderLeftWidth: 4,
        borderLeftColor: '#6366F1',
        padding: 12,
    },
    promptBoxHighContrast: {
        backgroundColor: '#000000',
        borderLeftColor: '#FFFF00',
        borderWidth: 2,
        borderColor: '#FFFF00',
    },
    promptLabel: { fontSize: 12, fontWeight: '800', color: '#6366F1', marginBottom: 2, letterSpacing: 0.5 },
    promptLabelHighContrast: { color: '#FFFF00' },
    promptText: { fontSize: 16, color: '#1E293B', lineHeight: 22, fontWeight: '600' },
    promptTextHighContrast: { color: '#FFFFFF' },

    vocabContainer: { gap: 6 },
    vocabLabel: { fontSize: 12, fontWeight: 'bold', color: '#0EA5E9', marginBottom: 2 },
    vocabLabelHighContrast: { color: '#FFFF00' },
    vocabRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    vocabChip: {
        backgroundColor: '#E0F2FE',
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 6,
    },
    vocabChipHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },
    vocabChipText: { fontSize: 14, fontWeight: '600', color: '#0284C7' },
    vocabChipTextHighContrast: { color: '#FFFF00' },

    canvasArea: {
        flex: 1,
        width: '100%',
        borderRadius: 18,
        overflow: 'hidden',
        backgroundColor: '#FAFAFA',
        minHeight: 200,
    },
    canvasAreaHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },

    footer: {
        flexDirection: 'row',
        paddingTop: 12,
        gap: 12,
        alignItems: 'center',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    clearBtn: {
        minWidth: 84,
        height: 58,
        minHeight: 56,
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#CBD5E1',
        backgroundColor: '#F8FAFC',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
    },
    clearBtnHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
    },
    clearTxt: { fontSize: 16, fontWeight: '700', color: '#64748B' },
    clearTxtHighContrast: { color: '#FFFF00' },
    submitBtn: {
        flex: 1,
        height: 60,
        minHeight: 58,
        backgroundColor: '#22c55e',
        borderRadius: 18,
        borderBottomWidth: 5,
        borderBottomColor: '#15803d',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: {
        backgroundColor: '#D1D5DB',
        borderBottomColor: '#9CA3AF',
        shadowOpacity: 0,
        elevation: 0,
    },
    submitTxt: { fontSize: 22, fontWeight: '900', color: '#FFFFFF' },
});
